import { get } from 'lodash';
import appConfig from 'appConfig';
import { getLogger } from '@cld/upload-widget-common';
import binaryToFile from 'utils/binaryToFile';
import { getFilePathProps } from 'utils/fileUtils';
import consts, { EXPORT_MIME_MAP, EXPORT_TYPE_EXT_MAP, GOOGLE_DRIVE_RESOURCE_HEADER_KEY } from '../data/consts';
import { composeCorpora, composeSearchQuery, composeOrderBy, composeDriveParams, prepareItems } from './helpers';

const logger = getLogger();

const driveConf = appConfig.google.drive;
const CLIENT_ID = driveConf.clientId;
const API_URL = driveConf.jsApi;
const GIS_URL = driveConf.gis;
const SCOPES = driveConf.scopes.join(' ');
const FETCH_URL = appConfig.google.fetchBase;

const { FILE_FIELDS } = consts;

let apiInitPromise = null;

const toObservable = (tc) => {
  const listeners = [];

  if (tc) {
    tc.callback = (resp) => {
      listeners.forEach((l) => l(resp));
    };

    tc.addListener = (listener) => {
      listeners.push(listener);
    };
  }

  return tc;
};

const oauth2Client = (() => {
  let tokenClient;

  return {
    init(options) {
      tokenClient = toObservable(self.google.accounts.oauth2.initTokenClient(options));
    },
    signIn: () =>
      new Promise((resolve, reject) => {
        tokenClient.addListener((resp) => {
          if (resp.error) {
            reject(resp);
          } else {
            resolve(resp);
          }
        });

        tokenClient.requestAccessToken({ prompt: 'consent' });
      }),
  };
})();

const loadGoogleApi = async () => {
  const loadScript = (id, src) =>
    new Promise((resolve) => {
      ((d, s, id) => {
        if (d.getElementById(id)) {
          return;
        }

        let js;
        const fjs = d.getElementsByTagName(s)[0];
        js = d.createElement(s);
        js.id = id;
        js.src = src;

        js.onload = () => {
          logger.log(`[gdriveService]: finished loading ${src}`);
          resolve(true);
        };

        fjs?.parentNode?.insertBefore(js, fjs);
      })(document, 'script', id);
    });

  logger.log(`[gdriveService]: about to load google api script from: ${API_URL}`);
  await loadScript('google-api', API_URL);

  logger.log(`[gdriveService]: api script loaded, about to load GIS from : ${GIS_URL}`);
  await loadScript('gis', GIS_URL);
  logger.log('[gdriveService]: GIS loaded, about to load client');
  await new Promise((resolve) => self.gapi.load('client', resolve));
  logger.log('[gdriveService]: client loaded');

  return true;
};

const initGapi = () => {
  if (!apiInitPromise) {
    apiInitPromise = loadGoogleApi();
  }

  return apiInitPromise;
};

const initAuthentication = (clientId, loginRequested) => {
  oauth2Client.init({
    client_id: clientId,
    scope: SCOPES,
  });

  return self.gapi.client.getToken();
};

const listDrives = (params) =>
  new Promise((resolve) => {
    self.gapi.client.drive.drives
      .list({
        pageSize: driveConf.treePageSize,
        ...params,
      })
      .execute(resolve);
  });

const basicNodeProps = (parentId, nodeId, parentDepth = 0) => ({
  parentId,
  nodeId,
  depth: parentDepth + 1,
});

export default (options = {}) => {
  const initialize = async (notifyNotAlreadySignedIn) => {
    // Load api.js and auth2 api
    await initGapi(); // Initialize google api.

    await self.gapi.client.init({});

    await self.gapi.client.load('drive', 'v3');

    return initAuthentication(options.clientId || CLIENT_ID);
  };

  const login = async () => {
    logger.log('[gdriveService]: about to log in user');

    try {
      await oauth2Client.signIn();

      return { success: true };
    } catch (resp) {
      logger.error('[gdriveService]: ERROR when trying to log in user! ', resp.error);

      return { success: false };
    }
  };

  const logout = () => {
    logger.log('[gdriveService]: about to logout user');

    self.google.accounts.oauth2.revoke(getAccessToken());
    self.gapi.client.setToken(null);
  };

  const getAuthInfo = async () => {
    const hasToken = !!getAccessToken();

    if (hasToken) {
      const {
        result: {
          user: { displayName: name },
        },
      } = await self.gapi.client.drive.about.get({ fields: 'user' });

      return { isSignedIn: true, user: { name } };
    }

    return { isSignedIn: false, user: null };
  };

  const getFiles = async (params) => {
    let results = {};
    try {
      const { cursor: pageToken, parentId, driveId, path = [] } = params;

      const apiParams = {
        pageSize: params.pageSize || driveConf.pageSize,
        fields: `files(${FILE_FIELDS.join(',')}), nextPageToken`,
        ...composeSearchQuery(params),
        ...composeOrderBy(params),
        ...composeCorpora(params),
        ...composeDriveParams(params),
        ...(pageToken ? { pageToken } : {}),
      };

      const apiResponse = await self.gapi.client.drive.files.list(apiParams);

      const accessToken = getAccessToken();

      results = {
        id: parentId,
        items: prepareItems(get(apiResponse, 'result.files', []), {
          driveId,
          path,
          accessToken,
        }),
        cursor: get(apiResponse, 'result.nextPageToken'),
        _apiResponse: apiResponse,
        _apiParams: apiParams,
      };

      logger.log('[gdriveService]: retrieved files from drive: ', results);
    } catch (e) {
      logger.error(
        '[gdriveService]: error retrieved files from drive: ',
        get(e, 'result.error.errors', [])
          .map((error) => `${error.message}:${error.locationType}:${error.location}`)
          .join(','),
        params
      );
    }

    return results;
  };

  const getFilesRecursively = async (folderId, folderName = '', pageToken = null) => {
    const isFolder = ({ mimeType }) => mimeType === 'application/vnd.google-apps.folder';
    const result = [];
    const query = `'${folderId}' in parents and trashed=false`;
    const response = await self.gapi.client.drive.files.list({
      q: query,
      pageToken,
      fields: `files(${FILE_FIELDS.join(',')}), nextPageToken`,
    });

    const files = prepareItems(get(response, 'result.files', []), {
      accessToken: getAccessToken(),
    });

    for (const file of files) {
      if (!isFolder(file)) {
        file.name = `${folderName ? `/${folderName}/${file.name}` : file.name}`;
        result.push(file);
      }
    }

    if (response.result.nextPageToken) {
      await getFilesRecursively(folderId, folderName, response.result.nextPageToken);
    }

    // Recursively list files in subfolders
    for (const file of files) {
      if (isFolder(file)) {
        result.push(...(await getFilesRecursively(file.id, `${folderName}/${file.name}`)));
      }
    }
    return result;
  };

  const getDrives = async ({ parentId, cursor: pageToken, orderBy = 'name' }) => {
    const apiParams = {
      pageSize: driveConf.pageSize,
      orderBy,
      ...(pageToken ? { pageToken } : {}),
    };

    const apiResponse = await self.gapi.client.drive.drives.list(apiParams);

    const results = {
      id: parentId,
      items: prepareItems(get(apiResponse, 'result.drives', [])),
      cursor: get(apiResponse, 'result.nextPageToken'),
      _apiResponse: apiResponse,
      _apiParams: apiParams,
    };

    logger.log('[gdriveService]: retrieved drives from drive: ', results);

    return results;
  };

  const getSharedDrives = async () => {
    const sharedDrives = await listDrives();
    const id = 'sharedDrives';
    const children = (sharedDrives.drives || []).map((drive) => ({
      ...drive,
      ...basicNodeProps(id, drive.id, 0),
    }));

    return {
      ...sharedDrives,
      children,
    };
  };

  const searchFiles = (params) => getFiles(params);

  const getResourceKeyHeaderValue = (file) => `${file.id}/${file.resourceKey}`;

  const generateImageFile = async (file) => {
    const fileData = await self.gapi.client.request({
      path: `${FETCH_URL}${file.id}`,
      method: 'GET',
      params: { fileId: file.id, alt: 'media' },
      headers: file.resourceKey ? { [GOOGLE_DRIVE_RESOURCE_HEADER_KEY]: getResourceKeyHeaderValue(file) } : {},
    });

    const binary = fileData.body;
    const name = file.name || fileData.headers.date;

    return await binaryToFile(binary, {
      name,
      type: fileData.headers['Content-Type'],
    });
  };

  const getFileName = (file) => {
    const fileExt = EXPORT_TYPE_EXT_MAP[file.extension];

    return (file.name || new Date()) + (fileExt || '');
  };

  const getUseExport = (file) => {
    const googleMimeType = EXPORT_MIME_MAP[file.extension];

    return googleMimeType ? `/export?mimeType=${googleMimeType}&` : '?';
  };

  const getAccessToken = () => {
    return self.gapi.client.getToken()?.access_token;
  };

  const getExportTypeExtension = (ext) => EXPORT_TYPE_EXT_MAP[ext] && EXPORT_TYPE_EXT_MAP[ext].substring(1);

  const getType = (file) => {
    const { mimeType, extension } = file;

    if (!extension || mimeType.endsWith(extension)) {
      return mimeType;
    }

    return mimeType.split('/')[0] + '/' + extension;
  };

  const generateFileObject = async (fileObject, shouldCreateBinary) => {
    const isImage = (mimeType) => mimeType?.indexOf('image') === 0;

    const accessToken = getAccessToken();
    const useExport = getUseExport(fileObject);
    const type = getExportTypeExtension(fileObject.extension) || getType(fileObject);
    const name = getFileName(fileObject);

    const resourceKeyHeaderParam = fileObject.resourceKey ? `\r\n${GOOGLE_DRIVE_RESOURCE_HEADER_KEY}: ${getResourceKeyHeaderValue(fileObject)}` : '';

    const { name: filenameOverride, folder } = getFilePathProps(name);

    return {
      file: `${FETCH_URL}${fileObject.id}${useExport}alt=media`,
      type,
      name,
      size: fileObject.size,
      headers: `Authorization: Bearer ${accessToken}${resourceKeyHeaderParam}`,
      thumbnail: fileObject.thumbnail,
      binary: isImage(fileObject.mimeType) && shouldCreateBinary ? await generateImageFile(fileObject) : undefined,
      filenameOverride,
      folder,
    };
  };

  const generateFiles = async (files, shouldCreateBinary) => await Promise.all(files.map((f) => generateFileObject(f, shouldCreateBinary)));

  const getRoot = async () => {
    const root = await self.gapi.client.drive.files.get({
      fileId: 'root',
    });

    return {
      ...root.result,
      children: {},
      depth: 0,
      nodeId: root.result.id,
    };
  };

  return {
    initialize,
    login,
    logout,
    getAuthInfo,
    getFiles,
    getDrives,
    searchFiles,
    generateFiles,
    getRoot,
    getSharedDrives,
    getFilesRecursively,
  };
};
